import { buildFeatureTopologySubEntityId } from '../../topology/TopologyReferenceBuilders';
import {
    FeatureReferenceBinding,
    type FeatureBindingResolveContext,
    type FeatureBindingResolvedValue,
    type SerializedModelTopologyFeatureBinding,
} from './FeatureReferenceBinding';
import { resolveModelTopologyOwnerResultMesh } from '../../utils/feature/resolveModelTopologyOwnerResultMesh';

/**
 * 克隆运行时面拓扑提示。
 *
 * @param faceHint 面提示。
 * @returns 纯数据副本。
 */
function cloneRuntimeTopologyFaceHint(
    faceHint: NonNullable<NonNullable<SelectionItemRef['runtimeTopologyHint']>['adjacentFaceHints']>[number],
): RuntimeTopologyFaceHint {
    return {
        subEntityId: faceHint.subEntityId,
        topologyIndex: faceHint.topologyIndex,
        shapeHash: faceHint.shapeHash,
        persistentRef: faceHint.persistentRef,
    };
}

/**
 * 克隆运行时拓扑提示。
 *
 * @param runtimeTopologyHint 运行时拓扑提示。
 * @returns 纯数据副本；不存在时返回 `undefined`。
 */
function cloneRuntimeTopologyHint(runtimeTopologyHint: SelectionItemRef['runtimeTopologyHint']): RuntimeTopologyHint | undefined {
    if (!runtimeTopologyHint) {
        return undefined;
    }

    return {
        kind: runtimeTopologyHint.kind,
        subEntityId: runtimeTopologyHint.subEntityId,
        topologyIndex: runtimeTopologyHint.topologyIndex,
        shapeHash: runtimeTopologyHint.shapeHash,
        sourceObjectId: runtimeTopologyHint.sourceObjectId,
        persistentRef: runtimeTopologyHint.persistentRef,
        adjacentFaceRefs: runtimeTopologyHint.adjacentFaceRefs
            ? [...runtimeTopologyHint.adjacentFaceRefs]
            : undefined,
        adjacentFaceHints: runtimeTopologyHint.adjacentFaceHints
            ? runtimeTopologyHint.adjacentFaceHints.map((faceHint) => cloneRuntimeTopologyFaceHint(faceHint))
            : undefined,
    };
}

/**
 * 克隆拓扑选择项。
 *
 * @param item 选择项。
 * @returns 纯数据副本。
 */
function cloneSelectionItemRef(item: SelectionItemRef): SelectionItemRef {
    return {
        kind: item.kind,
        bodyObjectId: item.bodyObjectId,
        subEntityId: item.subEntityId,
        persistentRef: item.persistentRef,
        owningFeatureId: item.owningFeatureId,
        adjacentFaceRefs: item.adjacentFaceRefs ? [...item.adjacentFaceRefs] : undefined,
        runtimeTopologyHint: cloneRuntimeTopologyHint(item.runtimeTopologyHint),
    };
}

/**
 * 克隆点引用。
 *
 * @param pointRef 点引用。
 * @returns 纯数据副本。
 */
function cloneBlendPointRef(pointRef: BlendPointRef): BlendPointRef {
    return {
        bodyObjectId: pointRef.bodyObjectId,
        subEntityId: pointRef.subEntityId,
        point: [...pointRef.point] as [number, number, number],
        persistentRef: pointRef.persistentRef,
        displayLabel: pointRef.displayLabel,
        runtimeTopologyHint: cloneRuntimeTopologyHint(pointRef.runtimeTopologyHint),
    };
}

/**
 * 判断两个候选字符串是否匹配。
 *
 * @param left 左值。
 * @param right 右值。
 * @returns 是否匹配。
 */
function isSameSignal(left: string | undefined | null, right: string | undefined | null): boolean {
    return typeof left === 'string'
        && left.length > 0
        && left === right;
}

/**
 * 读取结果体当前特征 ID。
 *
 * @param object 当前场景对象。
 * @param fallbackFeatureId 回退特征 ID。
 * @returns 可用于构建稳定子拓扑身份的特征 ID。
 */
function resolveIdentityFeatureId(
    object: { getFeatureMetadata: () => Record<string, unknown> },
    fallbackFeatureId: string,
): string {
    const metadata = object.getFeatureMetadata();
    const featureId = metadata.featureId;
    return typeof featureId === 'string' && featureId.length > 0
        ? featureId
        : fallbackFeatureId;
}

/**
 * 构建稳定子拓扑身份。
 *
 * @param kind 子拓扑类型。
 * @param featureId 当前结果体所属特征 ID。
 * @param topologyIndex 当前结果体内的拓扑索引。
 * @param fallbackSubEntityId 回退身份。
 * @returns 当前结果体 live 子拓扑身份。
 */
function resolveStableSubEntityId(
    kind: 'edge' | 'face' | 'vertex',
    featureId: string,
    topologyIndex: number,
    fallbackSubEntityId?: string,
): string {
    /*
     * Worker/离线包快照中的 subEntityId 是权威身份。当前结果体 featureId 只描述宿主修订，
     * 不能覆盖上游保留拓扑或离线包恢复出的稳定子拓扑身份。
     */
    if (typeof fallbackSubEntityId === 'string' && fallbackSubEntityId.length > 0) {
        return fallbackSubEntityId;
    }
    return typeof featureId === 'string' && featureId.length > 0
        ? buildFeatureTopologySubEntityId(kind, featureId, topologyIndex)
        : '';
}

/**
 * 读取边选择携带的全部匹配信号。
 *
 * @param item 原始选择项。
 * @returns 可能用于回绑 live 边的信号集合。
 */
function resolveSelectionSignals(item: SelectionItemRef): Array<string> {
    return [
        item.subEntityId,
        item.runtimeTopologyHint?.subEntityId,
        item.persistentRef,
        item.runtimeTopologyHint?.persistentRef,
    ].filter((signal): signal is string => typeof signal === 'string' && signal.length > 0);
}

/**
 * 读取边选择携带的相邻面信号。
 *
 * @param item 原始选择项。
 * @returns 去重后的相邻面引用集合。
 */
function resolveExpectedAdjacentFaceRefs(item: SelectionItemRef): string[] {
    const faceRefs = new Set<string>();
    (item.adjacentFaceRefs ?? []).forEach((faceRef) => {
        if (typeof faceRef === 'string' && faceRef.length > 0) {
            faceRefs.add(faceRef);
        }
    });
    (item.runtimeTopologyHint?.adjacentFaceRefs ?? []).forEach((faceRef) => {
        if (typeof faceRef === 'string' && faceRef.length > 0) {
            faceRefs.add(faceRef);
        }
    });
    (item.runtimeTopologyHint?.adjacentFaceHints ?? []).forEach((faceHint) => {
        if (typeof faceHint.persistentRef === 'string' && faceHint.persistentRef.length > 0) {
            faceRefs.add(faceHint.persistentRef);
        }
    });
    return [...faceRefs];
}

/**
 * 读取点引用携带的全部匹配信号。
 *
 * @param pointRef 原始点引用。
 * @returns 可能用于回绑 live 顶点的信号集合。
 */
function resolvePointSignals(pointRef: BlendPointRef): Array<string> {
    return [
        pointRef.subEntityId,
        pointRef.runtimeTopologyHint?.subEntityId,
        pointRef.persistentRef,
        pointRef.runtimeTopologyHint?.persistentRef,
    ].filter((signal): signal is string => typeof signal === 'string' && signal.length > 0);
}

/**
 * 根据原始选择项匹配 live 边快照。
 *
 * @param edges 当前结果体边快照。
 * @param item 原始选择项。
 * @returns 命中的边快照；未命中时返回 `null`。
 */
function resolveLiveEdgeSnapshot(
    edges: TopologySnapshotEdge[],
    item: SelectionItemRef,
): TopologySnapshotEdge | null {
    const runtimeHint = item.runtimeTopologyHint;
    if (
        runtimeHint?.kind === 'edge'
        && Number.isInteger(runtimeHint.topologyIndex)
        && runtimeHint.topologyIndex >= 0
        && runtimeHint.topologyIndex < edges.length
    ) {
        const hintedEdge = edges[runtimeHint.topologyIndex] ?? null;
        if (
            hintedEdge
            && (
                isSameSignal(hintedEdge.subEntityId, runtimeHint.subEntityId)
                || isSameSignal(hintedEdge.ref, runtimeHint.persistentRef)
                || resolveSelectionSignals(item).length <= 0
            )
        ) {
            return hintedEdge;
        }
    }

    const signals = resolveSelectionSignals(item);
    const signalMatchedEdge = edges.find((edge) => (
        signals.some((signal) => (
            isSameSignal(edge.subEntityId, signal)
            || isSameSignal(edge.ref, signal)
        ))
    )) ?? null;
    if (signalMatchedEdge) {
        return signalMatchedEdge;
    }

    /**
     * 边的几何 ref 和 subEntityId 同时漂移时，仍然要允许用“相邻面集合 + 同源 topologyIndex”回绑。
     * 否则圆角在草图尺寸变化后会把旧边定义直接带入 worker，最终炸成未找到边拓扑引用。
     */
    const expectedFaceRefs = resolveExpectedAdjacentFaceRefs(item);
    if (expectedFaceRefs.length <= 0) {
        return null;
    }
    const adjacentFaceMatchedEdges = edges.filter((edge) => (
        expectedFaceRefs.every((faceRef) => edge.owningFaceRefs.includes(faceRef))
    ));
    if (adjacentFaceMatchedEdges.length === 1) {
        return adjacentFaceMatchedEdges[0]!;
    }

    const hintedTopologyIndex = item.runtimeTopologyHint?.kind === 'edge'
        ? item.runtimeTopologyHint.topologyIndex
        : null;
    if (Number.isInteger(hintedTopologyIndex)) {
        return adjacentFaceMatchedEdges.find((edge) => edge.index === hintedTopologyIndex) ?? null;
    }
    return null;
}

/**
 * 根据原始选择项匹配 live 面快照。
 *
 * @param faces 当前结果体面快照。
 * @param item 原始选择项。
 * @returns 命中的面快照；未命中时返回 `null`。
 */
function resolveLiveFaceSnapshot(
    faces: TopologySnapshotFace[],
    item: SelectionItemRef,
): TopologySnapshotFace | null {
    const runtimeHint = item.runtimeTopologyHint;
    if (
        runtimeHint?.kind === 'face'
        && Number.isInteger(runtimeHint.topologyIndex)
        && runtimeHint.topologyIndex >= 0
        && runtimeHint.topologyIndex < faces.length
    ) {
        const hintedFace = faces[runtimeHint.topologyIndex] ?? null;
        if (
            hintedFace
            && (
                isSameSignal(hintedFace.subEntityId, runtimeHint.subEntityId)
                || isSameSignal(hintedFace.ref, runtimeHint.persistentRef)
                || resolveSelectionSignals(item).length <= 0
            )
        ) {
            return hintedFace;
        }
    }

    const signals = resolveSelectionSignals(item);
    return faces.find((face) => (
        signals.some((signal) => (
            isSameSignal(face.subEntityId, signal)
            || isSameSignal(face.ref, signal)
        ))
    )) ?? null;
}

/**
 * 判断顶点坐标是否与点引用足够接近。
 *
 * @param vertex 顶点快照。
 * @param pointRef 点引用。
 * @returns 是否命中。
 */
function matchesPointByPosition(
    vertex: TopologySnapshotVertex,
    pointRef: BlendPointRef,
): boolean {
    const tolerance = 1e-6;
    return Math.abs(vertex.position[0] - pointRef.point[0]) <= tolerance
        && Math.abs(vertex.position[1] - pointRef.point[1]) <= tolerance
        && Math.abs(vertex.position[2] - pointRef.point[2]) <= tolerance;
}

/**
 * 根据原始点引用匹配 live 顶点快照。
 *
 * @param vertices 当前结果体顶点快照。
 * @param pointRef 原始点引用。
 * @returns 命中的顶点快照；未命中时返回 `null`。
 */
function resolveLiveVertexSnapshot(
    vertices: TopologySnapshotVertex[],
    pointRef: BlendPointRef,
): TopologySnapshotVertex | null {
    const runtimeHint = pointRef.runtimeTopologyHint;
    if (
        runtimeHint?.kind === 'vertex'
        && Number.isInteger(runtimeHint.topologyIndex)
        && runtimeHint.topologyIndex >= 0
        && runtimeHint.topologyIndex < vertices.length
    ) {
        const hintedVertex = vertices[runtimeHint.topologyIndex] ?? null;
        if (
            hintedVertex
            && (
                isSameSignal(hintedVertex.subEntityId, runtimeHint.subEntityId)
                || isSameSignal(hintedVertex.ref, runtimeHint.persistentRef)
                || matchesPointByPosition(hintedVertex, pointRef)
            )
        ) {
            return hintedVertex;
        }
    }

    const signals = resolvePointSignals(pointRef);
    return vertices.find((vertex) => (
        signals.some((signal) => (
            isSameSignal(vertex.subEntityId, signal)
            || isSameSignal(vertex.ref, signal)
        ))
        || matchesPointByPosition(vertex, pointRef)
    )) ?? null;
}

/**
 * 构建 edge 选择的相邻面提示。
 *
 * @param faces 当前结果体面快照。
 * @param identityFeatureId 当前结果体特征 ID。
 * @param faceIndices 相邻面索引集合。
 * @returns 相邻面提示集合。
 */
function buildAdjacentFaceHints(
    faces: TopologySnapshotFace[],
    identityFeatureId: string,
    faceIndices: number[],
): RuntimeTopologyFaceHint[] {
    return faceIndices
        .map((faceIndex) => faces.find((face) => face.index === faceIndex) ?? null)
        .filter((face): face is TopologySnapshotFace => face !== null)
        .map((face) => ({
            subEntityId: resolveStableSubEntityId('face', identityFeatureId, face.index, face.subEntityId),
            topologyIndex: face.index,
            shapeHash: face.shapeHash,
            persistentRef: face.ref,
        }));
}

/**
 * 基于 live 边快照构建选择项。
 *
 * @param input 构建输入。
 * @returns live 选择项。
 */
export function buildLiveEdgeSelectionItem(input: {
    bodyObjectId: string;
    identityFeatureId: string;
    item: SelectionItemRef;
    edge: TopologySnapshotEdge;
    faces: TopologySnapshotFace[];
}): SelectionItemRef {
    const adjacentFaceHints = buildAdjacentFaceHints(
        input.faces,
        input.identityFeatureId,
        input.edge.adjacentFaceIndices,
    );
    const adjacentFaceRefs = adjacentFaceHints.map((faceHint) => faceHint.persistentRef);
    const stableSubEntityId = resolveStableSubEntityId(
        'edge',
        input.identityFeatureId,
        input.edge.index,
        input.edge.subEntityId,
    );
    const runtimeTopologyHint: RuntimeTopologyHint = {
        kind: 'edge',
        subEntityId: stableSubEntityId,
        topologyIndex: input.edge.index,
        shapeHash: input.edge.shapeHash,
        sourceObjectId: input.bodyObjectId,
        persistentRef: input.edge.ref,
        adjacentFaceRefs,
        adjacentFaceHints,
    };
    return {
        ...cloneSelectionItemRef(input.item),
        bodyObjectId: input.bodyObjectId,
        subEntityId: stableSubEntityId,
        // 语义名随选择项一并落入命令，使下游特征在上游尺寸变更后仍能定位同一条边；
        // live 快照未提供时保留原有语义名，避免降级快照把已有身份抹掉。
        semanticName: input.edge.semanticName ?? input.item.semanticName ?? undefined,
        persistentRef: input.edge.ref,
        adjacentFaceRefs,
        runtimeTopologyHint,
    };
}

/**
 * 基于 live 面快照构建选择项。
 *
 * @param input 构建输入。
 * @returns live 选择项。
 */
function buildLiveFaceSelectionItem(input: {
    bodyObjectId: string;
    identityFeatureId: string;
    item: SelectionItemRef;
    face: TopologySnapshotFace;
}): SelectionItemRef {
    const stableSubEntityId = resolveStableSubEntityId(
        'face',
        input.identityFeatureId,
        input.face.index,
        input.face.subEntityId,
    );
    const runtimeTopologyHint: RuntimeTopologyHint = {
        kind: 'face',
        subEntityId: stableSubEntityId,
        topologyIndex: input.face.index,
        shapeHash: input.face.shapeHash,
        sourceObjectId: input.bodyObjectId,
        persistentRef: input.face.ref,
    };
    return {
        ...cloneSelectionItemRef(input.item),
        bodyObjectId: input.bodyObjectId,
        subEntityId: stableSubEntityId,
        persistentRef: input.face.ref,
        runtimeTopologyHint,
    };
}

/**
 * 基于 live 顶点快照构建选择项。
 *
 * @param input 构建输入。
 * @returns live 选择项。
 */
function buildLiveVertexSelectionItem(input: {
    bodyObjectId: string;
    identityFeatureId: string;
    item: SelectionItemRef;
    vertex: TopologySnapshotVertex;
}): SelectionItemRef {
    const stableSubEntityId = resolveStableSubEntityId(
        'vertex',
        input.identityFeatureId,
        input.vertex.index,
        input.vertex.subEntityId,
    );
    const runtimeTopologyHint: RuntimeTopologyHint = {
        kind: 'vertex',
        subEntityId: stableSubEntityId,
        topologyIndex: input.vertex.index,
        sourceObjectId: input.bodyObjectId,
        persistentRef: input.vertex.ref,
    };
    return {
        ...cloneSelectionItemRef(input.item),
        bodyObjectId: input.bodyObjectId,
        subEntityId: stableSubEntityId,
        persistentRef: input.vertex.ref,
        runtimeTopologyHint,
    };
}

/**
 * 基于 live 顶点快照构建点引用。
 *
 * @param input 构建输入。
 * @returns live 点引用。
 */
function buildLivePointRef(input: {
    bodyObjectId: string;
    identityFeatureId: string;
    pointRef: BlendPointRef;
    vertex: TopologySnapshotVertex;
}): BlendPointRef {
    const stableSubEntityId = resolveStableSubEntityId(
        'vertex',
        input.identityFeatureId,
        input.vertex.index,
        input.vertex.subEntityId,
    );
    const runtimeTopologyHint: RuntimeTopologyHint = {
        kind: 'vertex',
        subEntityId: stableSubEntityId,
        topologyIndex: input.vertex.index,
        sourceObjectId: input.bodyObjectId,
        persistentRef: input.vertex.ref,
    };
    return {
        ...cloneBlendPointRef(input.pointRef),
        bodyObjectId: input.bodyObjectId,
        subEntityId: stableSubEntityId,
        persistentRef: input.vertex.ref,
        point: [...input.vertex.position] as [number, number, number],
        runtimeTopologyHint,
    };
}

/**
 * 模型拓扑绑定对象。
 */
export class ModelTopologyFeatureBinding extends FeatureReferenceBinding {
    /** 绑定类型。*/
    public readonly kind = 'model-topology' as const;

    /** 序列化载荷。*/
    private readonly payload: SerializedModelTopologyFeatureBinding;

    /**
     * @param payload 绑定载荷。
     */
    public constructor(payload: SerializedModelTopologyFeatureBinding) {
        super();
        this.payload = {
            ...payload,
            items: payload.items ? payload.items.map((item) => cloneSelectionItemRef(item)) : undefined,
            pointRefs: payload.pointRefs ? payload.pointRefs.map((pointRef) => cloneBlendPointRef(pointRef)) : undefined,
        };
    }

    /**
     * 从输入项创建绑定载荷。
     *
     * @param input 输入参数。
     * @returns 序列化绑定载荷。
     */
    public static createPayload(input: {
        bodyObjectId: string;
        items?: SelectionItemRef[];
        pointRefs?: BlendPointRef[];
    }): SerializedModelTopologyFeatureBinding {
        return {
            kind: 'model-topology',
            bodyObjectId: input.bodyObjectId,
            items: input.items ? input.items.map((item) => cloneSelectionItemRef(item)) : undefined,
            pointRefs: input.pointRefs ? input.pointRefs.map((pointRef) => cloneBlendPointRef(pointRef)) : undefined,
        };
    }

    /**
     * @inheritdoc
     */
    public serialize(): SerializedModelTopologyFeatureBinding {
        return {
            ...this.payload,
            items: this.payload.items ? this.payload.items.map((item) => cloneSelectionItemRef(item)) : undefined,
            pointRefs: this.payload.pointRefs ? this.payload.pointRefs.map((pointRef) => cloneBlendPointRef(pointRef)) : undefined,
        };
    }

    /**
     * @inheritdoc
     */
    public clone(): ModelTopologyFeatureBinding {
        return new ModelTopologyFeatureBinding(this.serialize());
    }

    /**
     * @inheritdoc
     */
    public dependsOnSketch(_sketchId: string): boolean {
        return false;
    }

    /**
     * @inheritdoc
     */
    public collectModelReferenceIds(): string[] {
        const references = new Set<string>();

        if (this.payload.bodyObjectId) {
            references.add(this.payload.bodyObjectId);
        }

        this.payload.items?.forEach((item) => {
            if (item.bodyObjectId) {
                references.add(item.bodyObjectId);
            }
            if (item.owningFeatureId) {
                references.add(item.owningFeatureId);
            }
        });

        this.payload.pointRefs?.forEach((pointRef) => {
            if (pointRef.bodyObjectId) {
                references.add(pointRef.bodyObjectId);
            }
        });

        return Array.from(references);
    }

    /**
     * @inheritdoc
     */
    public resolve(context: FeatureBindingResolveContext): FeatureBindingResolvedValue | null {
        const object = resolveModelTopologyOwnerResultMesh({
            scene: context.viewer.scene,
            ownerObjectId: this.payload.bodyObjectId,
            getSceneObjectById: context.viewer.getSceneObjectById.bind(context.viewer),
        });
        if (!object) {
            return {
                kind: 'model-topology',
                bodyObjectId: this.payload.bodyObjectId,
                object: null,
                items: this.payload.items ? this.payload.items.map((item) => cloneSelectionItemRef(item)) : [],
                pointRefs: this.payload.pointRefs ? this.payload.pointRefs.map((pointRef) => cloneBlendPointRef(pointRef)) : [],
            };
        }

        const topologySnapshot = object.getTopologySnapshot();
        if (!topologySnapshot) {
            return {
                kind: 'model-topology',
                bodyObjectId: this.payload.bodyObjectId,
                object,
                items: this.payload.items ? this.payload.items.map((item) => cloneSelectionItemRef(item)) : [],
                pointRefs: this.payload.pointRefs ? this.payload.pointRefs.map((pointRef) => cloneBlendPointRef(pointRef)) : [],
            };
        }

        const identityFeatureId = resolveIdentityFeatureId(object, this.payload.bodyObjectId);
        const items: SelectionItemRef[] = (this.payload.items ?? []).map((item) => {
            if (item.kind === 'edge') {
                const liveEdge = resolveLiveEdgeSnapshot(topologySnapshot.edges, item);
                return liveEdge
                    ? buildLiveEdgeSelectionItem({
                        bodyObjectId: this.payload.bodyObjectId,
                        identityFeatureId,
                        item,
                        edge: liveEdge,
                        faces: topologySnapshot.faces,
                    })
                    : cloneSelectionItemRef(item);
            }
            if (item.kind === 'face') {
                const liveFace = resolveLiveFaceSnapshot(topologySnapshot.faces, item);
                return liveFace
                    ? buildLiveFaceSelectionItem({
                        bodyObjectId: this.payload.bodyObjectId,
                        identityFeatureId,
                        item,
                        face: liveFace,
                    })
                    : cloneSelectionItemRef(item);
            }
            if (item.kind === 'vertex') {
                const liveVertex = resolveLiveVertexSnapshot(topologySnapshot.vertices, {
                    bodyObjectId: item.bodyObjectId,
                    subEntityId: item.subEntityId,
                    point: [0, 0, 0],
                    persistentRef: item.persistentRef,
                    runtimeTopologyHint: item.runtimeTopologyHint,
                });
                return liveVertex
                    ? buildLiveVertexSelectionItem({
                        bodyObjectId: this.payload.bodyObjectId,
                        identityFeatureId,
                        item,
                        vertex: liveVertex,
                    })
                    : cloneSelectionItemRef(item);
            }
            return cloneSelectionItemRef(item);
        });

        const pointRefs = (this.payload.pointRefs ?? []).map((pointRef) => {
            const liveVertex = resolveLiveVertexSnapshot(topologySnapshot.vertices, pointRef);
            return liveVertex
                ? buildLivePointRef({
                    bodyObjectId: this.payload.bodyObjectId,
                    identityFeatureId,
                    pointRef,
                    vertex: liveVertex,
                })
                : cloneBlendPointRef(pointRef);
        });

        return {
            kind: 'model-topology',
            bodyObjectId: this.payload.bodyObjectId,
            object,
            items,
            pointRefs,
        };
    }
}
